import { useState, useEffect, useCallback, useRef } from 'react';
import { txApi, triggerDownload } from '../txApi.js';
import { C, inp, btn, label, Err, Spinner } from './_shared.jsx';
import { VersionBar, ImpactDialog, STALE_MESSAGE, isStale } from './_versioning.jsx';
import { SERIES } from '../../../shared/styles/tokens.js';
import { useWorkspace } from '../../../shared/workspace/WorkspaceContext.jsx';
import { usablePlatforms } from '../platformAccess.js';

const DELIMITERS = ['_', '~', '|', '-', '.', ':', '#', '/'];
const DATE_FORMATS = ['YYYYMMDD', 'DDMMYYYY', 'MM-DD-YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD'];

const TYPE_ICON = { free_text: '✦', date: '📅', dropdown: '▾' };
const TYPE_LABEL = { free_text: 'Free text input', date: 'Date', dropdown: 'Dropdown' };

// ── Delimiter picker ──────────────────────────────────────────────────────────
function DelimPicker({ value, onChange, lbl }) {
  const [custom, setCustom] = useState('');
  const isCustom = !!value && !DELIMITERS.includes(value);
  return (
    <div className="o-ctrl">
      <span className="o-eyebrow">{lbl}</span>
      <div className="o-chipset">
        {DELIMITERS.map(d => (
          <button key={d} className="o-chip" aria-pressed={value === d} onClick={() => onChange(d)}>
            {d}
          </button>
        ))}
        <input
          className="o-chip o-wide"
          style={{ width: 74, textAlign: 'center' }}
          placeholder="custom"
          value={isCustom ? value : custom}
          onChange={e => { setCustom(e.target.value); onChange(e.target.value); }}
        />
      </div>
    </div>
  );
}

// ── Dictionary popover (inline expand) ───────────────────────────────────────
function DictPanel({ field, onDictChange }) {
  const [form, setForm] = useState({ value: '', label: '' });
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!form.value.trim() || !form.label.trim()) return;
    setSaving(true);
    try { await txApi.createDictEntry(field.id, form); setForm({ value: '', label: '' }); onDictChange(); }
    catch { }
    finally { setSaving(false); }
  };

  const remove = async (eid) => {
    try { await txApi.deleteDictEntry(eid); onDictChange(); }
    catch { }
  };

  return (
    <div style={{ padding: '10px 14px', background: C.bg, borderTop: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: C.muted, textTransform: 'uppercase', marginBottom: 8 }}>Dictionary Values</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 10 }}>
        {(field.dictionary || []).map(d => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: C.mono, fontSize: 11, color: C.code, background: C.codeWash, padding: '2px 7px', borderRadius: 6, minWidth: 60 }}>{d.value}</span>
            <span style={{ fontSize: 12, color: C.dim, flex: 1 }}>{d.label}</span>
            <button onClick={() => remove(d.id)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
          </div>
        ))}
        {!(field.dictionary || []).length && <div style={{ fontSize: 11, color: C.muted }}>No values yet.</div>}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input style={inp({ width: 90, fontFamily: C.mono })} placeholder="value" value={form.value}
          onChange={e => setForm(f => ({ ...f, value: e.target.value }))} />
        <input style={inp({ flex: 1 })} placeholder="label" value={form.label}
          onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
        <button style={btn('primary', { padding: '6px 14px' })} onClick={add} disabled={saving}>+ Add</button>
      </div>
    </div>
  );
}

const TYPE_HINT = {
  free_text: 'User types freely in this field.',
  date: 'User enters a date value.',
  dropdown: 'User selects from a predefined list.',
};

// ── Field form (create or edit mode) ─────────────────────────────────────────
const normKey = s => (s || '').trim().toLowerCase();

function FieldForm({ field, index, isKV, onSave, onCancel, isNew, clientId, siblings = [] }) {
  const [name,       setName]       = useState(field?.name || '');
  const [key,        setKey]        = useState(field?.field_key || '');
  const [type,       setType]       = useState(field?.field_type || 'free_text');
  const [required,   setRequired]   = useState(field?.is_required || false);
  const [dateFormat,      setDateFormat]      = useState(field?.date_format || 'YYYYMMDD');
  const [validationRegex, setValidationRegex] = useState(field?.validation_regex || '');
  const [regexError,      setRegexError]      = useState('');
  const [dictListId,      setDictListId]      = useState(field?.dict_list_id || '');
  const [dictLists,       setDictLists]       = useState([]);
  const [dictOpen,        setDictOpen]        = useState(false);
  const [saving,          setSaving]          = useState(false);

  useEffect(() => {
    if (type === 'dropdown') {
      const clientFetch = clientId
        ? txApi.listDictLists(clientId).then(d => d.lists || [])
        : Promise.resolve([]);
      clientFetch.then(lists => setDictLists(lists)).catch(() => {});
    }
  }, [type]);

  const validateRegex = (pattern) => {
    if (!pattern) { setRegexError(''); return true; }
    try { new RegExp(pattern); setRegexError(''); return true; }
    catch (e) { setRegexError(e.message); return false; }
  };

  // A label and a code each address one field within a level — in the preview, in the
  // generated string, and in every import that maps a column onto a field. Caught here so
  // the answer arrives while typing; the API rejects the same pair regardless.
  const others = siblings.filter(f => f.id !== field?.id);
  const nameClash = normKey(name)
    ? others.find(f => normKey(f.name) === normKey(name)) || null
    : null;
  const keyClash = isKV && normKey(key)
    ? others.find(f => normKey(f.field_key) === normKey(key)) || null
    : null;
  const dupError =
    nameClash ? 'Another field at this level already uses this label.' :
    keyClash ? `This code is already used by "${keyClash.name}".` : '';

  const handleSave = async () => {
    if (!name.trim() || nameClash || keyClash) return;
    if (type === 'free_text' && validationRegex && !validateRegex(validationRegex)) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        field_key: key.trim() || null,
        field_type: type,
        is_required: required,
        date_format: dateFormat,
        validation_regex: type === 'free_text' ? (validationRegex.trim() || null) : null,
        dict_list_id: type === 'dropdown' ? (dictListId || null) : null,
      });
    } finally { setSaving(false); }
  };

  return (
    <div style={{ border: `1px solid ${C.brass}66`, borderRadius: 12, marginBottom: 6, background: C.surface, overflow: 'hidden' }}>
      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px' }}>
        <div style={{ width: 22, textAlign: 'center', fontSize: 12, color: C.muted, flexShrink: 0 }}>{index + 1}</div>
        {isKV && (
          <input value={key} onChange={e => setKey(e.target.value)} placeholder="KEY"
            style={{ width: 52, height: 30, background: 'transparent',
              color: keyClash ? C.danger : key ? C.code : C.muted,
              border: `1px solid ${keyClash ? C.danger : key ? C.code : C.border}`, borderRadius: 8, textAlign: 'center',
              fontWeight: 700, fontSize: 11, fontFamily: C.mono, letterSpacing: '0.06em',
              cursor: 'text', outline: 'none', flexShrink: 0 }} />
        )}
        {/* Fixed-width label input — ~30 chars visible */}
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Field label" autoFocus
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onCancel(); }}
          style={{ width: 240, flexShrink: 0, background: 'transparent',
            border: `1px solid ${nameClash ? C.danger : C.border}`, borderRadius: 8,
            color: C.text, fontSize: 13, padding: '6px 10px', outline: 'none', fontFamily: 'inherit',
            transition: 'border-color 0.15s' }}
          onFocus={e => !nameClash && (e.target.style.borderColor = C.brass)}
          onBlur={e => !nameClash && (e.target.style.borderColor = C.border)}
        />
        {/* Type dropdown — takes remaining space */}
        <select value={type} onChange={e => setType(e.target.value)}
          style={{ flex: 1, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
            fontSize: 12, padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit', outline: 'none', height: 32 }}>
          <option value="free_text">✦ Free text input</option>
          <option value="date">📅 Date</option>
          <option value="dropdown">▾ Dropdown</option>
        </select>
        {/* Required — full label */}
        <button onClick={() => setRequired(r => !r)} style={{
          height: 30, padding: '0 12px', borderRadius: 12, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
          border: `1px solid ${required ? C.ink : C.border}`,
          background: required ? C.ink : 'transparent', color: required ? '#fff' : C.muted,
          flexShrink: 0, whiteSpace: 'nowrap',
        }}>Required</button>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}>×</button>
      </div>
      {dupError && (
        <div style={{ padding: '0 14px 8px 50px', fontSize: 11, color: C.danger }}>{dupError}</div>
      )}
      {/* Regex row — free text only */}
      {type === 'free_text' && (
        <div style={{ padding: '0 14px 10px 50px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>Validation regex</span>
            <input
              value={validationRegex}
              onChange={e => { setValidationRegex(e.target.value); validateRegex(e.target.value); }}
              placeholder="Optional — e.g. ^[A-Za-z0-9]+$"
              style={{
                flex: 1, background: 'transparent', fontFamily: C.mono,
                border: `1px solid ${regexError ? C.danger : C.border}`, borderRadius: 8,
                color: regexError ? C.danger : C.dim, fontSize: 12, padding: '5px 10px', outline: 'none',
              }}
              onFocus={e => !regexError && (e.target.style.borderColor = C.brass)}
              onBlur={e => !regexError && (e.target.style.borderColor = C.border)}
            />
          </div>
          {regexError && (
            <div style={{ fontSize: 11, color: C.danger, marginTop: 4, paddingLeft: 100 }}>{regexError}</div>
          )}
        </div>
      )}
      {/* Options row */}
      <div style={{ padding: '0 14px 12px 50px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: C.muted, flex: 1, minWidth: 180 }}>{TYPE_HINT[type]}</span>
        {type === 'date' && (
          <select value={dateFormat} onChange={e => setDateFormat(e.target.value)}
            style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 12, color: C.dim, fontSize: 11, padding: '3px 8px', fontFamily: 'inherit', outline: 'none' }}>
            {DATE_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        )}
        {type === 'dropdown' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>Data Dictionary list</span>
            <select value={dictListId} onChange={e => setDictListId(e.target.value)}
              style={{ flex: 1, background: C.s2, border: `1px solid ${dictListId ? C.brass : C.border}`,
                borderRadius: 8, color: dictListId ? C.text : C.muted, fontSize: 12,
                padding: '5px 10px', fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}>
              <option value="">— no list (custom values per field) —</option>
              {dictLists.map(l => (
                <option key={l.id} value={l.id}>{l._label || l.name} ({l.value_count} values)</option>
              ))}
            </select>
          </div>
        )}
        <button style={btn('ghost', { padding: '4px 12px', fontSize: 12 })} onClick={onCancel}>Cancel</button>
        <button style={btn('primary', { padding: '4px 16px', fontSize: 12 })} onClick={handleSave}
          disabled={saving || !name.trim() || !!nameClash || !!keyClash}>
          {isNew ? 'Create field' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Compact field row (view mode) ─────────────────────────────────────────────
function FieldRow({ field, index, isKV, isFirst, isLast, onEdit, onDelete, onMoveUp, onMoveDown }) {
  const dictCount = (field.dictionary || []).length;
  return (
    <div className="o-frow">
      <span className="o-num">{index + 1}</span>

      {/* Key code — only in KV mode */}
      {isKV && (
        <span style={{ width: 52, flexShrink: 0 }}>
          {field.field_key
            ? <span className="o-code">{field.field_key}</span>
            : <span style={{ display: 'inline-block', width: 40, height: 18,
                background: C.s2, borderRadius: 6, border: `1px solid ${C.border}` }} />}
        </span>
      )}

      <span className="o-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {field.name}
      </span>

      <span style={{ width: 36, flexShrink: 0, textAlign: 'center' }}>
        {field.is_required && <span className="o-badge">Req</span>}
      </span>

      <span style={{ width: 132, flexShrink: 0 }}>
        <span className="o-badge o-grey" style={{ whiteSpace: 'nowrap' }}>
          {TYPE_LABEL[field.field_type] || field.field_type}
          {field.field_type === 'dropdown' && dictCount > 0 ? ` · ${dictCount}` : ''}
        </span>
      </span>

      <span className="o-reorder">
        <button onClick={onMoveUp} disabled={isFirst} aria-label="Move up"
          style={{ opacity: isFirst ? 0.35 : 1, cursor: isFirst ? 'default' : 'pointer' }}>▲</button>
        <button onClick={onMoveDown} disabled={isLast} aria-label="Move down"
          style={{ opacity: isLast ? 0.35 : 1, cursor: isLast ? 'default' : 'pointer' }}>▼</button>
      </span>

      <button className="o-btn o-btn-sm" onClick={onEdit}>Edit</button>
      <button className="o-x" onClick={() => onDelete(field.id)} aria-label="Remove field">×</button>
    </div>
  );
}

// ── Copy Platform Modal ───────────────────────────────────────────────────────
function CopyPlatformModal({ platform, clientId, platforms, onClose, onCopied }) {
  const otherPlatforms = platforms.filter(p => String(p.id) !== String(platform.id));
  const [targetPlatformId, setTargetPlatformId] = useState(otherPlatforms[0]?.id || '');
  const [srcLevels,  setSrcLevels]  = useState([]);
  const [tgtLevels,  setTgtLevels]  = useState([]);
  const [levelMap,   setLevelMap]   = useState({}); // { srcLevelName: tgtLevelName | '' }
  const [conflicts,  setConflicts]  = useState(null);
  const [copying,    setCopying]    = useState(false);
  const [err,        setErr]        = useState('');

  // Load source platform levels once
  useEffect(() => {
    txApi.getPlatformLevels(platform.id)
      .then(d => setSrcLevels((d.levels || []).map(l => l.name)))
      .catch(() => {});
  }, [platform.id]);

  // Load target platform levels and auto-match whenever target changes
  useEffect(() => {
    if (!targetPlatformId) return;
    setConflicts(null); setErr('');
    txApi.getPlatformLevels(targetPlatformId)
      .then(d => {
        const names = (d.levels || []).map(l => l.name);
        setTgtLevels(names);
        // Auto-match by identical name
        setLevelMap(prev => {
          const next = {};
          srcLevels.forEach(src => {
            const prev_val = prev[src];
            next[src] = (prev_val !== undefined)
              ? prev_val
              : (names.includes(src) ? src : '');
          });
          return next;
        });
      })
      .catch(() => {});
  }, [targetPlatformId, srcLevels]);

  const doCopy = async (force = false) => {
    if (!targetPlatformId) return;
    // Build level_map, skipping empty mappings
    const map = {};
    srcLevels.forEach(src => { if (levelMap[src]) map[src] = levelMap[src]; });
    if (!Object.keys(map).length) { setErr('Map at least one level before copying.'); return; }
    setCopying(true); setErr(''); if (!force) setConflicts(null);
    try {
      await txApi.copyPlatform({
        source_platform_id: platform.id,
        client_id:          clientId,
        target_platform_id: targetPlatformId,
        level_map:          map,
        force,
      });
      onCopied();
    } catch (e) {
      if ((e.message || '').startsWith('Taxonomy already exists')) {
        const match = (e.message || '').match(/for:\s*(.+)$/);
        setConflicts(match ? match[1].split(',').map(s => s.trim()) : ['some levels']);
      } else {
        setErr(e.message || 'Copy failed');
      }
    } finally {
      setCopying(false);
    }
  };

  const mappedCount = srcLevels.filter(s => levelMap[s]).length;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: '28px 28px 24px', width: 500, maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>Copy Platform Taxonomy</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>
          Copy taxonomy from <strong>{platform.name}</strong> to another platform.
          Map each source level to the target level it should be copied into.
        </div>

        {otherPlatforms.length === 0 ? (
          <div style={{ fontSize: 13, color: C.muted, padding: '16px 0', textAlign: 'center' }}>
            No other platforms available for this client.
          </div>
        ) : (
          <>
            {/* Target platform */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.dim, letterSpacing: '0.07em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                Target Platform
              </label>
              <select value={targetPlatformId} onChange={e => setTargetPlatformId(e.target.value)}
                style={{ width: '100%', background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8,
                  color: C.text, fontSize: 13, padding: '8px 12px', fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}>
                {otherPlatforms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            {/* Level mapping */}
            {srcLevels.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1, fontSize: 11, fontWeight: 700, color: C.dim, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
                    Source Level ({platform.name})
                  </div>
                  <div style={{ width: 16 }} />
                  <div style={{ flex: 1, fontSize: 11, fontWeight: 700, color: C.dim, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
                    Target Level
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {srcLevels.map(src => (
                    <div key={src} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8,
                        padding: '7px 12px', fontSize: 13, color: C.text }}>
                        {src}
                      </div>
                      <span style={{ color: C.muted, fontSize: 16, flexShrink: 0 }}>→</span>
                      <select
                        value={levelMap[src] || ''}
                        onChange={e => setLevelMap(prev => ({ ...prev, [src]: e.target.value }))}
                        style={{ flex: 1, background: C.s2, border: `1px solid ${levelMap[src] ? C.brass + '88' : C.border}`,
                          borderRadius: 8, color: levelMap[src] ? C.text : C.muted, fontSize: 13,
                          padding: '7px 12px', fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}>
                        <option value="">— Skip —</option>
                        {tgtLevels.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                {tgtLevels.length === 0 && (
                  <div style={{ fontSize: 11, color: C.warn, marginTop: 8 }}>
                    Target platform has no levels defined — add levels in Settings first.
                  </div>
                )}
              </div>
            )}

            {/* Conflict warning */}
            {conflicts && (
              <div style={{ background: C.warn + '18', border: `1px solid ${C.warn}55`, borderRadius: 8,
                padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.warn, marginBottom: 6 }}>
                  Taxonomy already exists for:
                </div>
                <div style={{ fontSize: 12, color: C.text, marginBottom: 10 }}>
                  {conflicts.map(c => <span key={c} style={{ display: 'inline-block', background: C.s2,
                    border: `1px solid ${C.border}`, borderRadius: 8, padding: '1px 8px', marginRight: 6,
                    marginBottom: 4, fontSize: 11 }}>{c}</span>)}
                </div>
                <div style={{ fontSize: 12, color: C.muted }}>Replace the existing taxonomy for these levels?</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button onClick={() => doCopy(true)} disabled={copying}
                    style={{ ...btn('primary', { padding: '6px 16px', fontSize: 12 }), background: C.warn,
                      borderColor: C.warn, opacity: copying ? 0.6 : 1 }}>
                    {copying ? 'Replacing…' : 'Yes, Replace'}
                  </button>
                  <button onClick={() => setConflicts(null)} style={btn('ghost', { padding: '6px 14px', fontSize: 12 })}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {err && <div style={{ fontSize: 12, color: C.danger, marginBottom: 12 }}>{err}</div>}

            {!conflicts && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={onClose} style={btn('ghost', { padding: '8px 18px' })}>Cancel</button>
                <button onClick={() => doCopy(false)} disabled={copying || mappedCount === 0}
                  style={{ ...btn('primary', { padding: '8px 20px' }), opacity: copying || mappedCount === 0 ? 0.5 : 1 }}>
                  {copying ? 'Copying…' : `⊕ Copy ${mappedCount} Level${mappedCount !== 1 ? 's' : ''}`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Copy Level Modal ──────────────────────────────────────────────────────────
function CopyLevelModal({ level, clientId, currentPlatformId, platforms, onClose, onCopied }) {
  const [targetPlatformId, setTargetPlatformId] = useState(platforms[0]?.id || '');
  const [targetLevels,     setTargetLevels]     = useState([]);
  const [targetLevelName,  setTargetLevelName]  = useState('');
  const [levelsLoading,    setLevelsLoading]    = useState(false);
  const [copying,          setCopying]          = useState(false);
  const [err,              setErr]              = useState('');

  useEffect(() => {
    if (!targetPlatformId) return;
    setLevelsLoading(true); setTargetLevelName('');
    txApi.getPlatformLevels(targetPlatformId)
      .then(d => {
        const lvls = d.levels || [];
        setTargetLevels(lvls);
        setTargetLevelName(lvls[0]?.name || '');
      })
      .catch(() => setTargetLevels([]))
      .finally(() => setLevelsLoading(false));
  }, [targetPlatformId]);

  const handleCopy = async () => {
    if (!targetPlatformId || !targetLevelName) return;
    setCopying(true); setErr('');
    try {
      await txApi.copyLevel({
        source_client_id:   clientId,
        source_platform_id: currentPlatformId,
        source_level:       level,
        target_platform_id: targetPlatformId,
        target_level_name:  targetLevelName,
      });
      onCopied(targetLevelName);
    } catch (e) {
      setErr(e.message);
    } finally {
      setCopying(false);
    }
  };

  const canCopy = targetPlatformId && targetLevelName && !levelsLoading;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: '28px 28px 24px', width: 420, boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>Copy Level</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>
          Copies <strong>{level}</strong>'s taxonomy structure (fields, types, delimiters) to an existing level on another platform.
        </div>

        {platforms.length === 0 ? (
          <div style={{ fontSize: 13, color: C.muted, padding: '16px 0', textAlign: 'center' }}>
            No platforms available for this client.
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.dim, letterSpacing: '0.07em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                Target Platform
              </label>
              <select value={targetPlatformId} onChange={e => setTargetPlatformId(e.target.value)}
                style={{ width: '100%', background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8,
                  color: C.text, fontSize: 13, padding: '8px 12px', fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}>
                {platforms.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}{String(p.id) === String(currentPlatformId) ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.dim, letterSpacing: '0.07em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                Target Level
              </label>
              {levelsLoading ? (
                <div style={{ fontSize: 12, color: C.muted, padding: '8px 0' }}>Loading levels…</div>
              ) : targetLevels.length === 0 ? (
                <div style={{ fontSize: 12, color: C.danger, padding: '8px 0' }}>
                  No levels defined for this platform. Add levels in Settings → Platforms first.
                </div>
              ) : (
                <select value={targetLevelName} onChange={e => setTargetLevelName(e.target.value)}
                  style={{ width: '100%', background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8,
                    color: C.text, fontSize: 13, padding: '8px 12px', fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}>
                  {targetLevels.map(lv => <option key={lv.id} value={lv.name}>{lv.name}</option>)}
                </select>
              )}
            </div>

            {err && <div style={{ fontSize: 12, color: C.danger, marginBottom: 12 }}>{err}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={btn('ghost', { padding: '8px 18px' })}>Cancel</button>
              <button onClick={handleCopy} disabled={!canCopy || copying}
                style={{ ...btn('primary', { padding: '8px 20px' }), opacity: !canCopy || copying ? 0.5 : 1 }}>
                {copying ? 'Copying…' : '⊕ Copy Level'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function TaxonomySetup() {
  // The client is the active workspace, not a choice made on this page. `wsClient` carries
  // only { id, name }, so the full record — which platforms the client has — still comes from
  // the module's own client list; this resolves one against the other.
  const { clientId: wsClientId, client: wsClient } = useWorkspace();

  const [clients,     setClients]     = useState([]);
  const [allPlatforms,setAllPlatforms]= useState([]);
  const [selClient,   setSelClient]   = useState(null);
  const [selPlatId,   setSelPlatId]   = useState(null);
  const [levels,      setLevels]      = useState([]);
  const [selLevel,    setSelLevel]    = useState(null);
  const [taxonomy,    setTaxonomy]    = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [err,         setErr]         = useState(null);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [addingField,    setAddingField]    = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [levelsLoading,  setLevelsLoading]  = useState(false);
  const [copyingLevel,    setCopyingLevel]    = useState(null); // level name being copied
  const [copyingPlatform, setCopyingPlatform] = useState(null); // platform object being duplicated
  // Versioning. `version` is whichever version the bar has selected — the active one by
  // default. Selecting an archived version puts the whole editor into read-only.
  const [version,        setVersion]        = useState(null);
  const [versionRefresh, setVersionRefresh] = useState(0);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  // Spreadsheet round-trip for the level's field list. `importKey` remounts the file input
  // so re-uploading the same corrected file still fires a change event.
  const [exporting,  setExporting]  = useState(false);
  const [importing,  setImporting]  = useState(false);
  const [importMsg,  setImportMsg]  = useState(null);
  const [importKey,  setImportKey]  = useState(0);
  const importFileRef = useRef(null);
  const resolvedClientRef = useRef(null);

  const readOnly = !!version && version.status !== 'active';

  // load clients + platforms
  useEffect(() => {
    Promise.all([txApi.listClients(), txApi.listPlatforms()])
      .then(([c, p]) => { setClients(c.clients); setAllPlatforms(p.platforms); })
      .catch(e => setErr(e.message))
      .finally(() => setClientsLoading(false));
  }, []);

  // Follow the workspace: on load, and again whenever the top-bar selector changes.
  useEffect(() => {
    const match = clients.find(c => String(c.id) === String(wsClientId)) || null;
    setSelClient(match);
    // Switching client invalidates the platform, level and taxonomy below it — but merely
    // re-fetching the client list (after a platform copy, say) must not throw the selection
    // away, so the reset is keyed on the client actually changing.
    if (String(match?.id) !== String(resolvedClientRef.current)) {
      resolvedClientRef.current = match?.id ?? null;
      setSelPlatId(null); setSelLevel(null); setTaxonomy(null);
    }
  }, [wsClientId, clients]);

  // client's platforms — only the usable ones. A platform switched off globally or unassigned
  // from this client has no tab here, because a taxonomy edited on it could never be used.
  const clientPlatforms = usablePlatforms(selClient, allPlatforms);
  const firstPlatformId = clientPlatforms[0]?.id ?? null;

  // Platform tabs behave like tabs: one is always open. Nothing below them renders until a
  // platform is picked, so landing with none selected left the version bar and the level pills
  // hidden behind a click that had only one sensible answer. The level and version defaults
  // downstream (first level, active version) then follow on their own.
  useEffect(() => {
    if (selPlatId || !firstPlatformId) return;
    setSelPlatId(firstPlatformId);
  }, [firstPlatformId, selPlatId]);

  // when platform changes → load levels
  useEffect(() => {
    if (!selPlatId) { setLevels([]); setSelLevel(null); setTaxonomy(null); setLevelsLoading(false); return; }
    setLevelsLoading(true);
    txApi.getPlatformLevels(selPlatId)
      .then(d => {
        const lvls = d.levels || [];
        setLevels(lvls);
        // No implicit Campaign level any more. Standing one in gave a platform with no levels
        // a full editor: adding a field there created a taxonomy and a version against a level
        // that did not exist, and the campaign builder — which reads real levels — then had
        // nothing to offer. A platform with no levels now has nothing to configure.
        setSelLevel(lvls[0]?.name || null);
      })
      .catch(() => { setLevels([]); setSelLevel(null); })
      .finally(() => setLevelsLoading(false));
  }, [selPlatId]);

  // The version bar's selection scopes everything below it. Ignored while it still names the
  // previous platform's version — the bar reloads a beat after the platform tab changes, and
  // reading a foreign version id in that gap blanks the editor for a frame.
  const viewVersionId = version && String(version.platform_id) === String(selPlatId)
    ? version.id : null;

  // load taxonomy when client/platform/level all set
  const loadTaxonomy = useCallback(async () => {
    if (!selClient || !selPlatId || !selLevel) { setTaxonomy(null); return; }
    setLoading(true); setErr(null);
    try {
      const d = await txApi.getTaxonomy(selClient.id, selPlatId, selLevel, viewVersionId);
      setTaxonomy(d.taxonomy);
    }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [selClient, selPlatId, selLevel, viewVersionId]);

  useEffect(() => {
    loadTaxonomy(); setAddingField(false); setEditingFieldId(null); setImportMsg(null);
  }, [loadTaxonomy]);

  const upsertTaxonomy = async (patch) => {
    if (!selClient || !selPlatId || !selLevel) return;
    setSaving(true); setErr(null);
    try {
      const base = taxonomy || { taxonomy_type: 'position', field_delimiter: '_', kv_delimiter: null };
      await txApi.upsertTaxonomy(selClient.id, selPlatId, { ...base, ...patch, level: selLevel });
      loadTaxonomy();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const addField = async (fieldData) => {
    setErr(null);
    try {
      let tax = taxonomy;
      if (!tax) {
        const d = await txApi.upsertTaxonomy(selClient.id, selPlatId, {
          taxonomy_type: 'position', field_delimiter: '_', kv_delimiter: null, level: selLevel
        });
        tax = d.taxonomy;
      }
      await txApi.createField(tax.id, fieldData);
      setAddingField(false);
      loadTaxonomy();
    } catch (e) { setErr(e.message); }
  };

  const updateField = async (fid, patch) => {
    try { await txApi.updateField(fid, patch); setEditingFieldId(null); loadTaxonomy(); }
    catch (e) { setErr(e.message); }
  };

  // Removing a field is the destructive in-place edit: the values live on campaigns bound
  // to this version, and dropping the definition drops them. The dialog does the counting
  // and the confirming; this only runs once it has been agreed to.
  const deleteField = (fid) => {
    const field = (taxonomy?.fields || []).find(f => f.id === fid);
    if (!field) return;
    setPendingRemoval(field);
  };

  const confirmRemoval = async () => {
    try {
      await txApi.deleteField(pendingRemoval.id);
      // Record the edit against the revision we loaded. A 409 here means someone else
      // saved while this dialog was open.
      if (version?.id) await txApi.touchVersion(version.id, version.revision);
      setPendingRemoval(null);
      setVersionRefresh(n => n + 1);
      loadTaxonomy();
    } catch (e) {
      setPendingRemoval(null);
      setErr(isStale(e) ? STALE_MESSAGE : e.message);
    }
  };

  const moveField = async (idx, dir) => {
    const fields = taxonomy.fields || [];
    const other = idx + dir;
    if (other < 0 || other >= fields.length) return;
    try {
      await txApi.updateField(fields[idx].id, { sort_order: fields[other].sort_order });
      await txApi.updateField(fields[other].id, { sort_order: fields[idx].sort_order });
      loadTaxonomy();
    } catch (e) { setErr(e.message); }
  };

  // The sheet describes one level: the fields as shown, in order. Exporting the version the
  // bar has selected — not always the active one — so an archived field list can be read out.
  const exportFields = async () => {
    if (exporting || !selClient || !selPlatId || !selLevel) return;
    setExporting(true); setErr(null);
    try {
      const platformName = clientPlatforms.find(p => String(p.id) === String(selPlatId))?.name || '';
      const blob = await txApi.exportFields({
        client_id: selClient.id, platform_id: selPlatId, level: selLevel,
        client_name: selClient.name, platform_name: platformName,
        version_id: viewVersionId,
      });
      const slug = s => String(s || '').replace(/[^A-Za-z0-9-_]+/g, '_');
      triggerDownload(blob, `Taxonomy_${slug(selClient.name)}_${slug(platformName)}_${slug(selLevel)}.xlsx`);
    } catch (e) { setErr(e.message); }
    finally { setExporting(false); }
  };

  // Import only adds: a row whose label already exists at this level is skipped, so a sheet
  // can never remove a field that campaigns already hold values for.
  const importFields = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportMsg(null); setImporting(true); setErr(null);
    txApi.importFields(file, {
      client_id: selClient.id, platform_id: selPlatId, level: selLevel,
    })
      .then(data => {
        setImportMsg(data);
        if (data.ok) { setVersionRefresh(n => n + 1); loadTaxonomy(); }
      })
      .catch(e2 => setImportMsg({ errors: [e2.message] }))
      .finally(() => { setImporting(false); setImportKey(k => k + 1); });
  };

  const isKV = taxonomy?.taxonomy_type === 'keyvalue';
  const fields = taxonomy?.fields || [];
  const dictLinked = fields.filter(f => f.field_type === 'dropdown' && (f.dictionary || []).length > 0).length;

  // Preview, as renderable segments. Same delimiters and same placeholder rules
  // as before — split apart only so each part can be coloured independently.
  const previewParts = (() => {
    if (!taxonomy) return [];
    const fdel = taxonomy.field_delimiter || '_';
    const kvdel = taxonomy.kv_delimiter || '=';
    return fields.map(f => ({
      sep: fdel,
      key: isKV && f.field_key ? f.field_key : '',
      kvdel: isKV && f.field_key ? kvdel : '',
      placeholder: isKV
        ? f.name.replace(/\s+/g, '')
        : f.name.replace(/\s+/g, '').toUpperCase().slice(0, 2),
    }));
  })();

  return (
    <>
    {/* No fixed height and no inner scroller — the page itself scrolls. */}
    <div style={{ display: 'flex', gap: 0 }}>
      {/* The client comes from the workspace selector in the top bar — this page edits the
          taxonomy of whichever client is active, and offers no client picker of its own. */}
      {clientsLoading ? (
        <div style={{ flex: 1 }}><Spinner /></div>
      ) : !selClient ? (
        <div className="o-state" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {wsClient
            ? `${wsClient.name} is not available in the Taxonomy Generator. Add it in Settings → Clients.`
            : 'No workspace selected. Pick a client from the workspace selector in the top bar.'}
        </div>
      ) : (
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Client title */}
          <div style={{ fontSize: 24, fontWeight: 700, color: C.text, marginBottom: 16 }}>{selClient.name}</div>

          <Err msg={err} />

          {/* Platform tabs */}
          {clientPlatforms.length === 0 ? (
            <div className="o-hint">
              <b>No platforms available for this client.</b> Assign one in Settings → Clients,
              or reactivate it in Settings → Platforms if it was switched off there.
            </div>
          ) : (
            <nav className="o-subtabs">
              {clientPlatforms.map(p => (
                <button
                  key={p.id}
                  className="o-subtab"
                  aria-current={selPlatId === p.id ? 'page' : 'false'}
                  onClick={() => { setSelPlatId(p.id); setSelLevel(null); setTaxonomy(null); }}
                >
                  {p.name}
                  <span
                    className="o-add"
                    role="button"
                    tabIndex={0}
                    title="Duplicate this platform"
                    onClick={e => { e.stopPropagation(); setCopyingPlatform(p); }}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setCopyingPlatform(p); } }}
                  >+</span>
                </button>
              ))}
            </nav>
          )}

          {selPlatId && levelsLoading && <Spinner />}

          {/* A taxonomy is configured per level, so a platform with no levels has nothing to
              configure — no version, no delimiters, no fields. Levels are defined once for the
              platform in Settings, not per client here. */}
          {selPlatId && !levelsLoading && levels.length === 0 && (
            <div className="o-hint">
              <b>No levels defined for this platform.</b> A taxonomy is set up one level at a
              time, so there is nothing to configure until this platform has at least one. Add
              levels in Settings → Platforms.
            </div>
          )}

          {selPlatId && !levelsLoading && levels.length > 0 && (
            <>
              {/* Above the level tabs deliberately: a version covers every level of the
                  platform, so it cannot sit inside one level's editor. */}
              <VersionBar
                clientId={selClient?.id}
                platformId={selPlatId}
                refreshKey={versionRefresh}
                onVersionChange={setVersion}
              />

              {/* Level tabs. Levels themselves belong to the platform and are managed in
                  Settings → Platforms — this page selects among them, it does not create them. */}
              <div className="o-pills">
                {levels.map(lv => (
                  <button
                    key={lv.id}
                    className="o-pill-btn"
                    aria-pressed={selLevel === lv.name}
                    onClick={() => setSelLevel(lv.name)}
                  >
                    {lv.name}
                    <span
                      role="button"
                      tabIndex={0}
                      title="Copy to another platform"
                      style={{ opacity: 0.65 }}
                      onClick={e => { e.stopPropagation(); setCopyingLevel(lv.name); }}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setCopyingLevel(lv.name); } }}
                    >+</span>
                  </button>
                ))}
              </div>

              {loading && <Spinner />}

              {/* Spreadsheet round-trip. Deliberately outside the fieldset below: an
                  archived version is read-only to edit but still worth exporting. */}
              {!loading && selLevel && (
                <div className="o-head-row" style={{ marginBottom: 12 }}>
                  <span className="o-note">
                    The sheet holds this level's field list — {selLevel} on{' '}
                    {clientPlatforms.find(p => String(p.id) === String(selPlatId))?.name}.
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="o-btn" onClick={exportFields} disabled={exporting}>
                      {exporting ? 'Exporting…' : 'Export fields'}
                    </button>
                    <button className="o-btn" disabled={importing || readOnly}
                      onClick={() => importFileRef.current?.click()}>
                      {importing ? 'Importing…' : 'Import fields'}
                    </button>
                    <input key={importKey} ref={importFileRef} type="file" accept=".xlsx,.xls"
                      style={{ display: 'none' }} tabIndex={-1} aria-hidden="true"
                      disabled={importing || readOnly} onChange={importFields} />
                  </div>
                </div>
              )}

              {importMsg && (
                <div className="o-panel" style={{ borderColor: importMsg.errors ? C.danger : C.border,
                  padding: '12px 16px', marginBottom: 16, fontSize: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: importMsg.errors ? 8 : 0 }}>
                    <span style={{ fontWeight: 700, color: importMsg.errors ? C.danger : C.text }}>
                      {importMsg.errors
                        ? `Import blocked — ${importMsg.errors.length} error${importMsg.errors.length !== 1 ? 's' : ''} found`
                        : `Import complete — ${importMsg.imported} field${importMsg.imported !== 1 ? 's' : ''} added`
                          + (importMsg.skipped ? `, ${importMsg.skipped} already defined` : '')}
                    </span>
                    <div style={{ flex: 1 }} />
                    <button onClick={() => setImportMsg(null)} aria-label="Dismiss"
                      style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
                  </div>
                  {importMsg.errors ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {importMsg.errors.map((e, i) => (
                        <div key={i} style={{ color: C.danger, display: 'flex', gap: 6 }}>
                          <span style={{ flexShrink: 0 }}>✗</span><span>{e}</span>
                        </div>
                      ))}
                      <div style={{ marginTop: 6, color: C.muted, fontSize: 11 }}>
                        Nothing was written. Fix the rows above and upload again.
                      </div>
                    </div>
                  ) : importMsg.skipped ? (
                    <div style={{ marginTop: 6, color: C.muted }}>
                      Skipped, already at this level: {(importMsg.skipped_labels || []).join(', ')}
                    </div>
                  ) : null}
                </div>
              )}

              {!loading && selLevel && (
                <fieldset
                  disabled={readOnly}
                  style={{ border: 0, padding: 0, margin: 0, minWidth: 0,
                           opacity: readOnly ? 0.6 : 1 }}
                >
                  {/* Type + Delimiter config */}
                  <div className="o-panel o-ctrl-row">
                    {/* Type */}
                    <div className="o-ctrl">
                      <span className="o-eyebrow">Type</span>
                      <div className="o-toggle">
                        {['position', 'keyvalue'].map(t => (
                          <button key={t}
                            aria-selected={(taxonomy?.taxonomy_type || 'position') === t}
                            onClick={() => upsertTaxonomy({ taxonomy_type: t })}>
                            {t === 'position' ? 'Position' : 'Key-Value'}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Field parameter (KV only) — comes first: key~value */}
                    {isKV && (
                      <DelimPicker
                        lbl="Field parameter"
                        value={taxonomy?.kv_delimiter || '='}
                        onChange={v => upsertTaxonomy({ kv_delimiter: v })}
                      />
                    )}

                    {/* Separator — between fields */}
                    <DelimPicker
                      lbl="Separator"
                      value={taxonomy?.field_delimiter || '_'}
                      onChange={v => upsertTaxonomy({ field_delimiter: v })}
                    />
                  </div>

                  {/* Preview — the one ink surface on this screen. Keys are brass,
                      each value underlined in its own muted series colour. */}
                  <div className="o-preview">
                    <p className="o-eyebrow">Preview</p>
                    <div className="o-pstring">
                      {previewParts.length === 0
                        ? <span style={{ color: '#6E7683' }}>Add fields to see preview</span>
                        : previewParts.map((part, i) => (
                            <span key={i}>
                              {i > 0 && <span className="o-d">{part.sep}</span>}
                              {part.key && <>
                                <span className="o-k">{part.key}</span>
                                <span className="o-d">{part.kvdel}</span>
                              </>}
                              <span className="o-v" style={{ '--kc': SERIES[i % SERIES.length] }}>
                                {part.placeholder}
                              </span>
                            </span>
                          ))}
                    </div>
                  </div>

                  {/* Dict stats */}
                  {fields.length > 0 && (
                    <p className="o-note">
                      {dictLinked} of {fields.length} fields linked to the Data Dictionary.
                    </p>
                  )}

                  {/* Fields section */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', color: C.muted, textTransform: 'uppercase' }}>
                        Fields ({fields.length})
                      </div>
                      <button style={btn('primary', { padding: '6px 14px', fontSize: 12 })}
                        onClick={() => { setAddingField(true); setEditingFieldId(null); }}>
                        + Add Field
                      </button>
                    </div>

                    {addingField && (
                      <FieldForm
                        index={fields.length}
                        isKV={isKV}
                        isNew
                        clientId={selClient?.id}
                        siblings={fields}
                        onSave={addField}
                        onCancel={() => setAddingField(false)}
                      />
                    )}

                    {fields.length === 0 && !addingField && (
                      <div style={{ color: C.muted, fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
                        No fields yet — click <strong>+ Add Field</strong> to start.
                      </div>
                    )}

                    {fields.map((f, i) => (
                      editingFieldId === f.id ? (
                        <FieldForm
                          key={f.id}
                          field={f}
                          index={i}
                          isKV={isKV}
                          clientId={selClient?.id}
                          siblings={fields}
                          onSave={(patch) => updateField(f.id, patch)}
                          onCancel={() => setEditingFieldId(null)}
                        />
                      ) : (
                        <FieldRow
                          key={f.id}
                          field={f}
                          index={i}
                          isKV={isKV}
                          isFirst={i === 0}
                          isLast={i === fields.length - 1}
                          onEdit={() => { setEditingFieldId(f.id); setAddingField(false); }}
                          onDelete={deleteField}
                          onMoveUp={() => moveField(i, -1)}
                          onMoveDown={() => moveField(i, 1)}
                        />
                      )
                    ))}
                  </div>
                </fieldset>
              )}
            </>
          )}
        </div>
      )}
    </div>

    {/* Removing a field is destructive and irreversible: the values sit on campaigns bound
        to this version. The dialog states the cost in campaigns before anything is done. */}
    {pendingRemoval && version && (
      <ImpactDialog
        versionId={version.id}
        fields={[pendingRemoval]}
        action="remove"
        onCancel={() => setPendingRemoval(null)}
        onConfirm={confirmRemoval}
      />
    )}

    {copyingLevel && (
      <CopyLevelModal
        level={copyingLevel}
        clientId={selClient?.id}
        currentPlatformId={selPlatId}
        platforms={clientPlatforms}
        onClose={() => setCopyingLevel(null)}
        onCopied={() => setCopyingLevel(null)}
      />
    )}
    {copyingPlatform && (
      <CopyPlatformModal
        platform={copyingPlatform}
        clientId={selClient?.id}
        platforms={clientPlatforms}
        onClose={() => setCopyingPlatform(null)}
        onCopied={() => {
          setCopyingPlatform(null);
          // Reload clients+platforms so the new platform appears. Re-resolving the active
          // client against the fresh list is the workspace effect's job.
          Promise.all([txApi.listClients(), txApi.listPlatforms()])
            .then(([c, p]) => { setClients(c.clients); setAllPlatforms(p.platforms); })
            .catch(() => {});
        }}
      />
    )}
    </>
  );
}
